import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Accordion, Row } from 'react-bootstrap'

import { useAuth } from '../../../Auth'
import { useFetch } from '../../../Common'
import { useNotifications } from '../../../Notifications'

import { View } from '../../components/View'
import { Tile } from '../../components/Tile'

import { ContentGetResponse, PackageContentType } from '../../../../../cz.vegilove.api/src/_api-models'

import { API_URL, CONTENT_SOURCES } from '../../AppConfig'
import { ContentDefinitionWithLink, User } from '../../AppTypes'

import noThumb from '../../../assets/images/no-thumb.png'

type AlbumNames = { [ P in PackageContentType ]: string }

export const MyContent = () => {

    const intl = useIntl()

    const auth = useAuth<User>()
    const user = auth.getUser()

    const { addNotification } = useNotifications()

    const [ albums, setAlbums ] = useState<PackageContentType[]>()
    const [ data, setData ] = useState<(ContentGetResponse & { tileDefinition: ContentDefinitionWithLink})[]>()
    const albumNames = useMemo<AlbumNames>( () => ({
        'CORE': intl.formatMessage({ description: 'App.MyContent.Core', defaultMessage: 'Your content' }),
        'COMPLIMENTARY': intl.formatMessage({ description: 'App.MyContent.Complimentary', defaultMessage: 'Bonus content' })
    }), [ intl ])

    const dataRequest = useFetch<unknown, (ContentGetResponse & { tileDefinition: ContentDefinitionWithLink} )[], ContentGetResponse[]>({
        url: API_URL + '/content',
        cacheKey: `mycontent-${ user?.contactId || 'XXX' /* will never be used; uder must exists - no un-authorized access allowed */ }`,
        noAutoRun: !user,
        onData: ( rows ) => {
            setAlbums( rows.reduce<PackageContentType[]>( ( prev, curr ) => { 
                if( !prev.includes( curr.package_content_type ) ) prev.push( curr.package_content_type )
                return prev
            }, [] ) ) 
            return rows.map( c => { 
                // search attachments for type thumb which represents thumbnail
                // if there are no attachments or no thumb was found use "no-thumb.png"
                const thumb = (c.attachments && c.attachments.find( a => a.type === 'thumb' )) || { filename: noThumb.src, source: null }
                const content = (c.attachments && c.attachments.find( a => a.type !== 'thumb' )) || { source: 'none', filename: '' }
                return {
                    ...c,
                    tileDefinition: {
                        key: c.contentId,
                        title: c.name,
                        imgUrl: thumb && thumb.source ? CONTENT_SOURCES[ thumb.source ] + thumb?.filename : thumb?.filename,
                        to: ( c.type === 'VIDEO' || c.type === 'AUDIO' ? '/play/' : '/download/' ) + c.contentId.toString()
                    } as ContentDefinitionWithLink
                }
            })
        },
        onSuccess: state => {
            if( state.success && state.data ) setData( state.data )
            else addNotification({ variant: 'danger', category: 'MyContent.Data', content: intl.formatMessage({ description: '', defaultMessage: '' }) })
        }
    }, [ auth ])

    return <View>
        { data && albums && <Accordion defaultActiveKey={ albums[ 0 ] } alwaysOpen flush>
            { albums.map( album =>
                <Accordion.Item key={ album } eventKey={ album }>
                    <Accordion.Header>{ albumNames[ album ] }</Accordion.Header>
                    <Accordion.Body>
                        <Row>{ data.filter( c => c.package_content_type === album && c.locale === intl.locale ).map( c => <Tile 
                            key={ c.tileDefinition.key } 
                            tile={ c.tileDefinition } 
                            className='MyContent'
                            progress={ user && user.used ? (user?.used[ c.contentId ] || 0) * 100 : undefined } 
                            favicon
                        /> ) }</Row>
                    </Accordion.Body>
                </Accordion.Item>
            ) }
        </Accordion> }

    </View>
}

export default MyContent